import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import {
  buildOptimisticGarageVehicleComment,
  type OptimisticCommentAuthor,
  serverCommentMatchesOptimisticComment,
} from '@/src/features/comments/optimistic-comment';
import {
  createGarageSharePost,
  createGarageVehicle,
  createGarageVehicleComment,
  deleteGarageVehicle,
  deleteGarageVehicleComment,
  editGarageVehicleComment,
  getGarageVehicle,
  getGarageVehicleComments,
  getGarageVehiclePhotos,
  getPublicGarageVehicle,
  searchVehicleCatalog,
  setGarageVehicleCommentReaction,
  setGarageVehicleReaction,
  subscribeToGarageVehicle,
  subscribeToGarageVehicleCommentReaction,
  subscribeToGarageVehicleReaction,
  subscribeToUserGarageVehicles,
  updateGarageVehicle,
  type GarageVehicle,
  type GarageVehicleComment,
  type GarageVehicleFormInput,
  type GarageVehiclePhoto,
  type CreateGarageVehicleCommentInput,
  type VehicleCatalogOption,
  type VehicleType,
} from '@/src/features/garage/garage-service';
import type { Reaction } from '@/src/features/posts/post-service';
import { assert } from '@/src/utils/assert';

export function userGarageVehiclesQueryKey(uid: string | undefined) {
  return ['garage-vehicles', uid] as const;
}

export function garageVehicleQueryKey(vehicleId: string | undefined) {
  return ['garage-vehicle', vehicleId] as const;
}

export function garageVehiclePhotosQueryKey(vehicleId: string | undefined) {
  return ['garage-vehicle-photos', vehicleId] as const;
}

export function garageVehicleCommentsQueryKey(vehicleId: string | undefined) {
  return ['garage-vehicle-comments', vehicleId] as const;
}

type CreateGarageVehicleCommentMutationInput = CreateGarageVehicleCommentInput &
  OptimisticCommentAuthor;

const optimisticGarageCommentsByScopeKey = new Map<
  string,
  GarageVehicleComment[]
>();
const optimisticGarageCommentListenersByScopeKey = new Map<
  string,
  Set<() => void>
>();

function optimisticGarageCommentScopeKey(
  vehicleId: string | undefined,
  uid: string | undefined,
) {
  return vehicleId && uid ? `${vehicleId}:${uid}` : null;
}

function getOptimisticGarageComments(
  vehicleId: string | undefined,
  uid: string | undefined,
) {
  const scopeKey = optimisticGarageCommentScopeKey(vehicleId, uid);
  return scopeKey
    ? (optimisticGarageCommentsByScopeKey.get(scopeKey) ?? [])
    : [];
}

function notifyOptimisticGarageCommentListeners(scopeKey: string) {
  optimisticGarageCommentListenersByScopeKey
    .get(scopeKey)
    ?.forEach((listener) => listener());
}

function setOptimisticGarageComments(
  vehicleId: string,
  uid: string,
  updater: (comments: GarageVehicleComment[]) => GarageVehicleComment[],
) {
  const scopeKey = optimisticGarageCommentScopeKey(vehicleId, uid);

  if (!scopeKey) {
    return;
  }

  const nextComments = updater(
    optimisticGarageCommentsByScopeKey.get(scopeKey) ?? [],
  );

  if (nextComments.length > 0) {
    optimisticGarageCommentsByScopeKey.set(scopeKey, nextComments);
  } else {
    optimisticGarageCommentsByScopeKey.delete(scopeKey);
  }

  notifyOptimisticGarageCommentListeners(scopeKey);
}

function subscribeToOptimisticGarageComments(
  vehicleId: string,
  uid: string,
  listener: () => void,
) {
  const scopeKey = optimisticGarageCommentScopeKey(vehicleId, uid);

  if (!scopeKey) {
    return () => {};
  }

  const listeners =
    optimisticGarageCommentListenersByScopeKey.get(scopeKey) ??
    new Set<() => void>();
  listeners.add(listener);
  optimisticGarageCommentListenersByScopeKey.set(scopeKey, listeners);

  return () => {
    listeners.delete(listener);

    if (listeners.size === 0) {
      optimisticGarageCommentListenersByScopeKey.delete(scopeKey);
    }
  };
}

function addOptimisticGarageComment(
  input: CreateGarageVehicleCommentMutationInput,
) {
  const optimisticComment = buildOptimisticGarageVehicleComment(input);

  setOptimisticGarageComments(input.vehicleId, input.authorUid, (comments) => [
    optimisticComment,
    ...comments,
  ]);

  return optimisticComment.id;
}

function removeOptimisticGarageComment(
  vehicleId: string,
  uid: string,
  commentId: string,
) {
  setOptimisticGarageComments(vehicleId, uid, (comments) =>
    comments.filter((comment) => comment.id !== commentId),
  );
}

function reconcileOptimisticGarageComments(
  vehicleId: string,
  uid: string,
  serverComments: GarageVehicleComment[],
) {
  setOptimisticGarageComments(vehicleId, uid, (comments) =>
    comments.filter(
      (optimistic) =>
        !serverComments.some((server) =>
          serverCommentMatchesOptimisticComment({ optimistic, server }),
        ),
    ),
  );
}

function mergeUniqueGarageComments(...commentGroups: GarageVehicleComment[][]) {
  const seenIds = new Set<string>();
  const comments: GarageVehicleComment[] = [];

  commentGroups.flat().forEach((comment) => {
    if (seenIds.has(comment.id)) {
      return;
    }

    seenIds.add(comment.id);
    comments.push(comment);
  });

  return comments;
}

export function publicGarageVehicleQueryKey(
  username: string | undefined,
  vehicleSlug: string | undefined,
) {
  return ['public-garage-vehicle', username, vehicleSlug] as const;
}

export function vehicleCatalogQueryKey(input: {
  kind: 'makes' | 'models';
  make?: string;
  query?: string;
  type?: VehicleType;
  year?: number | null;
}) {
  return ['vehicle-catalog', input] as const;
}

export function useUserGarageVehicles(uid: string | undefined, enabled = true) {
  const [state, setState] = useState<{
    error: Error | null;
    loading: boolean;
    vehicles: GarageVehicle[];
  }>({
    error: null,
    loading: Boolean(uid && enabled),
    vehicles: [],
  });

  useEffect(() => {
    if (!uid || !enabled) {
      setState({ error: null, loading: false, vehicles: [] });
      return undefined;
    }

    setState((currentState) => ({
      ...currentState,
      error: null,
      loading: true,
    }));

    return subscribeToUserGarageVehicles({
      uid,
      onError: (error) =>
        setState((currentState) => ({
          ...currentState,
          error,
          loading: false,
        })),
      onNext: (vehicles) => setState({ error: null, loading: false, vehicles }),
    });
  }, [enabled, uid]);

  return state;
}

export function useGarageVehicle(vehicleId: string | undefined) {
  return useQuery({
    queryKey: garageVehicleQueryKey(vehicleId),
    enabled: Boolean(vehicleId),
    queryFn: async () => {
      assert(vehicleId, 'Vehicle ID is required.');
      return getGarageVehicle(vehicleId);
    },
  });
}

export function useRealtimeGarageVehicle(vehicleId: string | undefined) {
  const [state, setState] = useState<{
    error: Error | null;
    loading: boolean;
    vehicle: GarageVehicle | null;
  }>({
    error: null,
    loading: Boolean(vehicleId),
    vehicle: null,
  });

  useEffect(() => {
    if (!vehicleId) {
      setState({ error: null, loading: false, vehicle: null });
      return undefined;
    }

    setState((currentState) => ({
      ...currentState,
      error: null,
      loading: true,
    }));

    return subscribeToGarageVehicle({
      vehicleId,
      onError: (error) =>
        setState((currentState) => ({
          ...currentState,
          error,
          loading: false,
        })),
      onNext: (vehicle) => setState({ error: null, loading: false, vehicle }),
    });
  }, [vehicleId]);

  return state;
}

export function useGarageVehiclePhotos(vehicleId: string | undefined) {
  const photosQuery = useQuery<GarageVehiclePhoto[]>({
    queryKey: garageVehiclePhotosQueryKey(vehicleId),
    enabled: Boolean(vehicleId),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      assert(vehicleId, 'Vehicle ID is required.');
      return getGarageVehiclePhotos(vehicleId);
    },
  });

  return {
    error: photosQuery.error,
    loading: photosQuery.isLoading,
    photos: photosQuery.data ?? [],
  };
}

export function useGarageVehicleComments(
  vehicleId: string | undefined,
  uid?: string | undefined,
) {
  const [optimisticComments, setOptimisticComments] = useState<
    GarageVehicleComment[]
  >(() => getOptimisticGarageComments(vehicleId, uid));
  const commentsQuery = useQuery<GarageVehicleComment[]>({
    queryKey: garageVehicleCommentsQueryKey(vehicleId),
    enabled: Boolean(vehicleId),
    staleTime: 60 * 1000,
    queryFn: async () => {
      assert(vehicleId, 'Vehicle ID is required.');
      return getGarageVehicleComments({ vehicleId });
    },
  });

  useEffect(() => {
    if (!vehicleId || !uid) {
      setOptimisticComments([]);
      return undefined;
    }

    setOptimisticComments(getOptimisticGarageComments(vehicleId, uid));

    return subscribeToOptimisticGarageComments(vehicleId, uid, () => {
      setOptimisticComments(getOptimisticGarageComments(vehicleId, uid));
    });
  }, [vehicleId, uid]);

  useEffect(() => {
    if (!vehicleId || !uid || !commentsQuery.data) {
      return;
    }

    reconcileOptimisticGarageComments(vehicleId, uid, commentsQuery.data);
  }, [commentsQuery.data, vehicleId, uid]);

  return {
    comments: mergeUniqueGarageComments(
      optimisticComments,
      commentsQuery.data ?? [],
    ),
    error: commentsQuery.error,
    loading: commentsQuery.isLoading,
  };
}

export function usePublicGarageVehicle(
  username: string | undefined,
  vehicleSlug: string | undefined,
) {
  return useQuery({
    queryKey: publicGarageVehicleQueryKey(username, vehicleSlug),
    enabled: Boolean(username && vehicleSlug),
    queryFn: async () => {
      assert(username, 'Username is required.');
      assert(vehicleSlug, 'Vehicle slug is required.');
      return getPublicGarageVehicle({ username, vehicleSlug });
    },
  });
}

export function useVehicleCatalog(input: {
  enabled?: boolean;
  kind: 'makes' | 'models';
  make?: string;
  query?: string;
  type?: VehicleType;
  year?: number | null;
}) {
  return useQuery<VehicleCatalogOption[]>({
    queryKey: vehicleCatalogQueryKey(input),
    enabled: input.enabled !== false,
    queryFn: () => searchVehicleCatalog(input),
  });
}

export function useCreateGarageVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createGarageVehicle,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['garage-vehicles'] });
    },
  });
}

export function useUpdateGarageVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateGarageVehicle,
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['garage-vehicles'] }),
        queryClient.invalidateQueries({
          queryKey: garageVehicleQueryKey(result.vehicleId),
        }),
        queryClient.invalidateQueries({
          queryKey: garageVehiclePhotosQueryKey(result.vehicleId),
        }),
      ]);
    },
  });
}

export function useDeleteGarageVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteGarageVehicle,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['garage-vehicles'] });
    },
  });
}

export function useCreateGarageSharePost() {
  return useMutation({
    mutationFn: createGarageSharePost,
  });
}

export function useGarageVehicleReaction(
  vehicleId: string | undefined,
  uid: string | undefined,
) {
  const [state, setState] = useState<{
    error: Error | null;
    loading: boolean;
    mutationId: string | null;
    reaction: Reaction | null;
  }>({
    error: null,
    loading: Boolean(vehicleId && uid),
    mutationId: null,
    reaction: null,
  });

  useEffect(() => {
    if (!vehicleId || !uid) {
      setState({
        error: null,
        loading: false,
        mutationId: null,
        reaction: null,
      });
      return undefined;
    }

    setState((currentState) => ({
      ...currentState,
      error: null,
      loading: true,
    }));

    return subscribeToGarageVehicleReaction({
      uid,
      vehicleId,
      onError: (error) =>
        setState((currentState) => ({
          ...currentState,
          error,
          loading: false,
        })),
      onNext: (reaction, mutationId) =>
        setState({ error: null, loading: false, mutationId, reaction }),
    });
  }, [uid, vehicleId]);

  return state;
}

export function useSetGarageVehicleReaction() {
  return useMutation({
    mutationFn: setGarageVehicleReaction,
  });
}

export function useCreateGarageVehicleComment() {
  const queryClient = useQueryClient();

  return useMutation<
    Awaited<ReturnType<typeof createGarageVehicleComment>>,
    Error,
    CreateGarageVehicleCommentMutationInput,
    {
      optimisticCommentId: string;
      authorUid: string;
      vehicleId: string;
    }
  >({
    mutationFn: (input) => createGarageVehicleComment(input),
    onMutate: (input) => {
      return {
        authorUid: input.authorUid,
        optimisticCommentId: addOptimisticGarageComment(input),
        vehicleId: input.vehicleId,
      };
    },
    onError: (_error, _input, context) => {
      if (context) {
        removeOptimisticGarageComment(
          context.vehicleId,
          context.authorUid,
          context.optimisticCommentId,
        );
      }
    },
    onSuccess: async (_result, _input, context) => {
      if (!context) {
        return;
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: garageVehicleCommentsQueryKey(context.vehicleId),
        }),
        queryClient.invalidateQueries({
          queryKey: garageVehicleQueryKey(context.vehicleId),
        }),
      ]);

      setTimeout(() => {
        removeOptimisticGarageComment(
          context.vehicleId,
          context.authorUid,
          context.optimisticCommentId,
        );
      }, 30_000);
    },
  });
}

export function useDeleteGarageVehicleComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteGarageVehicleComment,
    onSuccess: async (_result, input) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: garageVehicleCommentsQueryKey(input.vehicleId),
        }),
        queryClient.invalidateQueries({
          queryKey: garageVehicleQueryKey(input.vehicleId),
        }),
      ]);
    },
  });
}

export function useEditGarageVehicleComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editGarageVehicleComment,
    onSuccess: async (_result, input) => {
      await queryClient.invalidateQueries({
        queryKey: garageVehicleCommentsQueryKey(input.vehicleId),
      });
    },
  });
}

export function useGarageVehicleCommentReaction({
  commentId,
  uid,
  vehicleId,
}: {
  commentId: string | undefined;
  uid: string | undefined;
  vehicleId: string | undefined;
}) {
  const [state, setState] = useState<{
    error: Error | null;
    loading: boolean;
    mutationId: string | null;
    reaction: Reaction | null;
  }>({
    error: null,
    loading: Boolean(commentId && uid && vehicleId),
    mutationId: null,
    reaction: null,
  });

  useEffect(() => {
    if (!commentId || !uid || !vehicleId) {
      setState({
        error: null,
        loading: false,
        mutationId: null,
        reaction: null,
      });
      return undefined;
    }

    return subscribeToGarageVehicleCommentReaction({
      commentId,
      uid,
      vehicleId,
      onError: (error) =>
        setState((currentState) => ({
          ...currentState,
          error,
          loading: false,
        })),
      onNext: (reaction, mutationId) =>
        setState({ error: null, loading: false, mutationId, reaction }),
    });
  }, [commentId, uid, vehicleId]);

  return state;
}

export function useSetGarageVehicleCommentReaction() {
  return useMutation({
    mutationFn: setGarageVehicleCommentReaction,
  });
}

export function vehicleFormDefaults(
  vehicle: GarageVehicle | null | undefined,
): GarageVehicleFormInput {
  return {
    coverPhotoId: vehicle?.coverPhoto?.id ?? null,
    make: vehicle?.make ?? '',
    model: vehicle?.model ?? '',
    mods: vehicle?.mods ?? [],
    nickname: vehicle?.nickname ?? '',
    ownershipStatus: vehicle?.ownershipStatus ?? 'current',
    photos: [],
    story: vehicle?.story ?? '',
    trim: vehicle?.trim ?? '',
    type: vehicle?.type ?? 'car',
    year: vehicle?.year ?? null,
  };
}
